import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useCartStore, useLanguageStore } from '../store';
import { translations } from '../translations';
import { supabase } from '../lib/supabase';
import { ShoppingCart, Trash2, MapPin, Truck, Shovel, AlertCircle, Globe, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const NURSERY_ADDRESS = 'Białogórne 5, 96-230 Biała Rawska';
const NURSERY_COORDS = { lat: 51.8097, lng: 20.4753 };
const TRANSPORT_COST_PER_KM = 5;
const PLANTING_COST_PER_TREE = 30;
const WHOLESALE_MINIMUM = 2000;

const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22 viewBox=%220 0 80 80%22%3E%3Crect fill=%22%23e2e8f0%22 width=%2280%22 height=%2280%22/%3E%3C/svg%3E';

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.ceil(R * c * 1.3);
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wasCancelled = searchParams.get('cancelled') === 'true';
  const { items, removeItem, updateQuantity, clearCart, isWholesale } = useCartStore();
  const language = useLanguageStore((state) => state.language);
  const t = translations[language];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    vatNumber: '',
    deliveryDate: '',
    additionalInfo: '',
  });

  const [pickupInPerson, setPickupInPerson] = useState(false);
  const [requiresPlanting, setRequiresPlanting] = useState(false);
  const [distanceKm, setDistanceKm] = useState<number>(0);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (pickupInPerson) {
      setDistanceKm(0);
      setRequiresPlanting(false);
    }
  }, [pickupInPerson]);

  const geocodeAddress = useCallback(async (address: string) => {
    if (!address || address.length < 5) {
      setDistanceKm(0);
      setGeocodingStatus('idle');
      return;
    }

    setGeocodingStatus('loading');
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        { headers: { 'Accept-Language': 'pl' } }
      );
      const results = await response.json();

      if (results && results.length > 0) {
        const { lat, lon } = results[0];
        const distance = haversineDistance(NURSERY_COORDS.lat, NURSERY_COORDS.lng, parseFloat(lat), parseFloat(lon));
        setDistanceKm(distance);
        setGeocodingStatus('success');
      } else {
        setGeocodingStatus('error');
        setDistanceKm(0);
      }
    } catch {
      setGeocodingStatus('error');
      setDistanceKm(0);
    }
  }, []);

  useEffect(() => {
    if (pickupInPerson || !formData.address) return;
    const timeout = setTimeout(() => geocodeAddress(formData.address), 1000);
    return () => clearTimeout(timeout);
  }, [formData.address, pickupInPerson, geocodeAddress]);

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalTrees = items.reduce((sum, item) => sum + item.quantity, 0);
  const transportCost = pickupInPerson ? 0 : distanceKm * TRANSPORT_COST_PER_KM;
  const plantingCost = requiresPlanting ? totalTrees * PLANTING_COST_PER_TREE : 0;
  const totalAmount = subtotal + transportCost + plantingCost;

  const canPlaceOrder = isWholesale ? totalAmount >= WHOLESALE_MINIMUM : true;

  const handleImageError = (key: string) => {
    setImgErrors(prev => new Set(prev).add(key));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (items.length === 0) {
      toast.error(t.checkout.emptyCart);
      return;
    }

    if (!acceptTerms) {
      toast.error(t.checkout.acceptTermsRequired);
      return;
    }

    if (isWholesale && totalAmount < WHOLESALE_MINIMUM) {
      toast.error(t.checkout.wholesaleMinimumError);
      return;
    }

    if (!pickupInPerson && distanceKm === 0) {
      toast.error(t.checkout.distanceRequired);
      return;
    }

    setLoading(true);

    try {
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: pickupInPerson ? NURSERY_ADDRESS : formData.address,
          vat_number: formData.vatNumber || null,
          delivery_date: formData.deliveryDate,
          requires_transport: !pickupInPerson,
          requires_planting: requiresPlanting,
          additional_info: formData.additionalInfo || null,
          total_amount: totalAmount,
          is_wholesale: isWholesale,
          transport_cost: transportCost,
          planting_cost: plantingCost,
          distance_km: distanceKm,
          pickup_in_person: pickupInPerson,
          status: 'pending',
        })
        .select()
        .single();

      if (orderError) throw orderError;

      const orderItems = items.map((item) => ({
        order_id: order.id,
        plant_id: item.plantId,
        plant_name: item.plantName,
        height: item.height,
        price: item.price,
        quantity: item.quantity,
      }));

      const { error: itemsError } = await supabase.from('order_items').insert(orderItems);
      if (itemsError) throw itemsError;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            orderId: order.id,
            items: items.map((item) => ({
              name: `${item.plantName} - ${item.height}`,
              plantId: item.plantId,
              height: item.height,
              quantity: item.quantity,
            })),
            isWholesale,
            pickupInPerson,
            requiresPlanting,
            distanceKm,
            email: formData.email,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Payment session error');
      }

      const { url } = await response.json();

      if (!url) throw new Error('No checkout URL returned');

      clearCart();
      window.location.href = url;
    } catch (error: any) {
      console.error('Order error:', error);
      toast.error(t.checkout.orderError);
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center py-16">
          <ShoppingCart className="w-20 h-20 mx-auto text-gray-400 dark:text-gray-600 mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t.checkout.emptyCartTitle}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">{t.checkout.emptyCartMessage}</p>
          <button
            onClick={() => navigate('/katalog')}
            className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            {t.checkout.browseCatalog}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {wasCancelled && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 dark:text-red-300">
              {language === 'pl' ? 'Płatność została anulowana' : 'Payment was cancelled'}
            </p>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
              {language === 'pl'
                ? 'Twoje zamówienie nie zostało opłacone. Możesz je edytować i spróbować ponownie.'
                : 'Your order was not paid. You can edit it and try again.'}
            </p>
          </div>
        </div>
      )}
      <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
        {t.checkout.title}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {t.checkout.cartItems}
            </h2>
            <div className="space-y-3">
              {items.map((item) => {
                const itemKey = `${item.plantId}-${item.height}`;
                return (
                  <div
                    key={itemKey}
                    className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <img
                      src={imgErrors.has(itemKey) ? FALLBACK_IMAGE : (item.imageUrl || FALLBACK_IMAGE)}
                      alt={item.plantName}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      onError={() => handleImageError(itemKey)}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {language === 'en' && item.plantNameEn ? item.plantNameEn : item.plantName}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.height}</p>
                      <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                        {item.price.toFixed(2)} zł
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateQuantity(item.plantId, item.height, parseInt(e.target.value) || 1)
                        }
                        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <button
                        onClick={() => removeItem(item.plantId, item.height)}
                        className="text-red-500 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {t.checkout.contactInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.checkout.firstName} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.checkout.lastName} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.checkout.email} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.checkout.phone} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.checkout.vatNumber}
                </label>
                <input
                  type="text"
                  value={formData.vatNumber}
                  onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t.checkout.vatNumberPlaceholder}
                />
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                <Truck className="w-5 h-5" />
                {t.checkout.deliverySection}
              </h2>

              <label className="flex items-center space-x-3 mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors">
                <input
                  type="checkbox"
                  checked={pickupInPerson}
                  onChange={(e) => setPickupInPerson(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-green-600"
                />
                <MapPin className="w-5 h-5 text-green-600" />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">{t.checkout.pickupInPerson}</span>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{NURSERY_ADDRESS}</p>
                </div>
              </label>

              {!pickupInPerson && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <Globe className="w-4 h-4 flex-shrink-0 text-blue-600" />
                    <span>{t.checkout.europeDeliveryInfo}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t.checkout.deliveryAddress} *
                    </label>
                    <input
                      type="text"
                      required={!pickupInPerson}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={t.checkout.addressPlaceholder}
                    />
                    {geocodingStatus === 'loading' && (
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                        <span className="animate-spin inline-block w-3 h-3 border border-gray-400 border-t-transparent rounded-full"></span>
                        {t.checkout.calculatingDistance}
                      </p>
                    )}
                    {geocodingStatus === 'success' && distanceKm > 0 && (
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        {t.checkout.estimatedDistance}: {distanceKm} km = {(distanceKm * TRANSPORT_COST_PER_KM).toFixed(2)} zł
                      </p>
                    )}
                    {geocodingStatus === 'error' && (
                      <div className="mt-2">
                        <p className="text-sm text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {t.checkout.geocodeError}
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t.checkout.manualDistance}
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={distanceKm || ''}
                            onChange={(e) => setDistanceKm(parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {t.checkout.transportCostInfo}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!pickupInPerson && (
              <div>
                <label className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={requiresPlanting}
                    onChange={(e) => setRequiresPlanting(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-green-600"
                  />
                  <Shovel className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{t.checkout.requiresPlanting}</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t.checkout.plantingCostInfo}</p>
                  </div>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.checkout.deliveryDate} *
              </label>
              <input
                type="date"
                required
                value={formData.deliveryDate}
                onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t.checkout.additionalInfo}
              </label>
              <textarea
                rows={3}
                value={formData.additionalInfo}
                onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder={t.checkout.additionalInfoPlaceholder}
              />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-green-600 mt-0.5"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t.checkout.termsLabel}{' '}
                  <Link to="/regulamin" className="text-green-600 dark:text-green-400 underline hover:no-underline" target="_blank">
                    {t.checkout.termsLink}
                  </Link>{' '}
                  {t.checkout.termsAnd}{' '}
                  <Link to="/polityka-prywatnosci" className="text-green-600 dark:text-green-400 underline hover:no-underline" target="_blank">
                    {t.checkout.privacyLink}
                  </Link> *
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !canPlaceOrder || !acceptTerms}
              className="w-full bg-green-600 text-white py-3.5 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {loading ? t.checkout.processing : t.checkout.proceedToPayment}
            </button>

            {isWholesale && !canPlaceOrder && (
              <p className="text-red-600 dark:text-red-400 text-sm text-center flex items-center justify-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {t.checkout.wholesaleMinimumWarning}
              </p>
            )}
          </form>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 sticky top-24">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {t.checkout.orderSummary}
            </h2>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                <span>{t.checkout.subtotal} ({totalTrees} {t.checkout.trees})</span>
                <span className="font-medium">{subtotal.toFixed(2)} zł</span>
              </div>
              {!pickupInPerson && distanceKm > 0 && (
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                  <span>{t.checkout.transport} ({distanceKm} km)</span>
                  <span className="font-medium">{transportCost.toFixed(2)} zł</span>
                </div>
              )}
              {requiresPlanting && (
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                  <span>{t.checkout.planting} ({totalTrees} x {PLANTING_COST_PER_TREE} zł)</span>
                  <span className="font-medium">{plantingCost.toFixed(2)} zł</span>
                </div>
              )}
              {pickupInPerson && (
                <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                  <span>{t.checkout.transport}</span>
                  <span>{t.checkout.free}</span>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between text-xl font-bold text-gray-900 dark:text-white">
                <span>{t.checkout.total}</span>
                <span>{totalAmount.toFixed(2)} zł</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.checkout.vatIncluded}</p>
            </div>

            {isWholesale && (
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-800 dark:text-blue-300 font-medium">
                  {t.checkout.wholesaleInfo}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
